import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { LocalStorage } from 'angular-web-storage';

import { environment } from '../../../environments/environment';
import { MyAccount } from '../../models/my-account';
import { OrganizationConfigService } from '../organization-config/organization-config.service';
import { InboxService } from '../inbox/inbox.service';
import { ApiService } from '../api/api.service';
import { LocationService } from '../location/location.service';
import { CapitanLocation } from '../../models/location';

/**
 * Describes the shape of the API /get-token/ response.
 */
export interface APITokenResponse {
  token: string;
}

/**
 * Describes the shape of the API /user/otp/generate/ response.
 */
export interface GenerateOTPResponse {
  otp_secret_key: string;
  otp_auth_url: string;
}

@Injectable({
  providedIn: 'root'
})
export class AccountService {
  /**
   * A service providing access to user accounts via the API.
   */

  // Whether the app is in customer-facing mode, in which case sensitive
  // information will be hidden. Once the app is placed in customer-facing mode,
  // it may only exit that mode by logging out.
  // Only used by the check in and check out sites.
  @LocalStorage() private inCustomerFacingMode = false;

  // The user's account information is *not* stored locally, and is instead
  // retrieved from the API when the site is loaded.
  // The user's account information is not directly available to components;
  // instead, a deep copy must be obtained via getMyAccount. This is to prevent
  // components from making non-API-synced edits which would affect other
  // components.
  private myAccount: MyAccount = null;

  // The server error code experienced when trying to load account info, if
  // any. This is not directly available to components; it must be obtained via
  // getMyAccountServerError.
  private myAccountServerError: number = null;

  // Whether a login is currently in progress. This helps differentiate between
  // an account info load from a stored token and an account info load
  // resulting from a login event.
  private loginInProgress = false;

  // Whether the user is currently inputting a one-time password for two-factor
  // authentication.
  private otpInProgress = false;

  // Whether the user is currently selecting their location. This is required
  // after logging in.
  private selectLocationInProgress = false;

  constructor(
    private http: HttpClient,
    private apiService: ApiService,
    private orgConfigService: OrganizationConfigService,
    private inboxService: InboxService,
    private locationService: LocationService
  ) {
    if (this.isLoggedIn()) {
      this.loadMyAccountInfo().then();
      this.orgConfigService.loadOrganizationConfig().then();
      this.inboxService.loadInboxCounts(false).then();
      this.locationService.loadLocations().then();
    }

    // Periodically update the inbox count values.
    setInterval(() => this.onInboxRefreshTick(), 5 * 60 * 1000);
  }

  /** No poll for a hidden tab: nobody is looking at the badge (PRO-1513). */
  private onInboxRefreshTick(): void {
    if (document.visibilityState === 'hidden') {
      return;
    }
    if (this.isLoggedIn()) {
      this.inboxService.loadInboxCounts(false).then();
    }
  }

  /**
   * Return whether the user has authentication credentials, and if they do,
   * ensure that the API auth header is injected.
   *
   * @return Whether the user is logged in.
   */
  public isLoggedIn(): boolean {
    return (
      (this.apiService.token !== null) &&
      !this.loginInProgress &&
      !this.otpInProgress &&
      !this.selectLocationInProgress
    );
  }

  /**
   * Return whether the user's account info has been loaded. This happens after
   * the initial login, and on application load if a token was stored.
   */
  public myAccountInfoIsLoaded(): boolean {
    return (this.myAccount !== null);
  }

  /**
   * Return a deep copy of the user's account info. A deep copy is used to
   * prevent components from making non-API-synced edits which would affect
   * other components.
   */
  public getMyAccount(): MyAccount {
    if (!this.myAccountInfoIsLoaded()) { return null; }

    const accountCopy = {};

    // Note: this only works as long as MyAccount has no nested objects.
    for (const key of Object.keys(this.myAccount)) {
      accountCopy[key] = this.myAccount[key];
    }

    return accountCopy as MyAccount;
  }

  /**
   * Return the server error obtained when attempting to load the user's
   * account, if any.
   */
  public getMyAccountServerError(): number {
    return this.myAccountServerError;
  }

  /**
   * Attempt to log the user in using the provided credentials. If successful,
   * save the user's token in local storage and inject the API auth header.
   *
   * @param email - The email.
   * @param password - The password.
   * @param usePinMode - If true, obtain a safe-only token and set the
   *     apiService to use PIN mode.
   * @param supportPinMode - Whether PIN mode is supported by the site into
   *     which the user is logging.
   * @return Whether the login succeeded.
   */
  public login(email: string, password: string, usePinMode: boolean, supportPinMode: boolean): Promise<void> {
    this.loginInProgress = true;

    let apiUrl: string;
    if (usePinMode) {
      apiUrl = environment.api_url + 'get-safe-token/';
    } else {
      apiUrl = environment.api_url + 'get-token/';
    }

    let params = new HttpParams();
    if (!supportPinMode) {
      params = params.set('pin_mode_not_supported', 'true');
    }

    return this.http
      .post<APITokenResponse>(
        apiUrl,
        {email, password},
        {
          headers: environment.api_headers,
          params
        }
      )
      .toPromise()
      .then((tokenData: APITokenResponse) => {
        // Clear old authentication info (if any) before saving new info.
        this.logout();

        // Save the token to local storage.
        this.apiService.token = tokenData.token;
        this.apiService.inPinMode = usePinMode;

        // Return the promise resulting from loadMyAccountInfo. We set the in
        // progress flag to false in a finally so that promise rejections can
        // still be handled by the caller.
        return this.loadMyAccountInfo()
          .finally(() => {
            this.loginInProgress = false;
          });
      });
  }

  /**
   * Log in without credentials as a local development user (used by the
   * /local-login URL). Only functional when running locally: the backend
   * get-local-token endpoint exists only when the server runs with
   * CENV=local.
   *
   * @return Whether the login succeeded.
   */
  public localLogin(): Promise<void> {
    this.loginInProgress = true;

    return this.http
      .post<APITokenResponse>(
        environment.api_url + 'get-local-token/',
        {},
        {headers: environment.api_headers}
      )
      .toPromise()
      .then((tokenData: APITokenResponse) => {
        // Clear old authentication info (if any) before saving new info.
        this.logout();

        // Save the token to local storage.
        this.apiService.token = tokenData.token;
        this.apiService.inPinMode = false;

        return this.loadMyAccountInfo()
          .then(() => {
            // The normal login flow ends with an interactive location
            // confirmation step; confirm the account's default location
            // automatically instead.
            if (this.selectLocationInProgress) {
              return this.setDefaultLocation(this.myAccount.default_gym_id);
            }
          })
          .finally(() => {
            this.loginInProgress = false;
          });
      })
      .catch(reason => {
        this.loginInProgress = false;
        throw reason;
      });
  }

  public loginOtp(token: string) {
    this.otpInProgress = true;

    return this.apiService
      .postToAPI<string>(
        'user/otp/obtain-token/',
        {token}
      )
      .then(otpAPIToken => {
        this.apiService.otpToken = otpAPIToken;

        this.otpInProgress = false;

        // Load the user's organization config, inbox counts, and locations.
        this.orgConfigService.loadOrganizationConfig().then();
        this.inboxService.loadInboxCounts(false).then();
        this.locationService.loadLocations().then();
        this.selectLocationInProgress = true;
      });
  }

  /**
   * Log the user out by removing their stored authentication credentials.
   */
  public logout() {
    this.apiService.token = null;
    this.apiService.otpToken = null;
    this.apiService.inPinMode = false;
    this.myAccount = null;
    this.myAccountServerError = null;
    this.inCustomerFacingMode = false;
    this.otpInProgress = false;
    this.selectLocationInProgress = false;

    // This second '= null' is required for it to update immediately.
    this.apiService.token = null;
    this.apiService.otpToken = null;
    this.apiService.inPinMode = false;
    this.myAccount = null;
    this.myAccountServerError = null;
    this.inCustomerFacingMode = false;
    this.otpInProgress = false;
    this.selectLocationInProgress = false;
  }

  /**
   * Load the user's account info from the API.
   */
  public loadMyAccountInfo(): Promise<void> {
    return this.apiService
      .getFromAPI<MyAccount>(
        'my-account/'
      )
      .then((accountData: MyAccount) => {
        this.myAccount = accountData;

        if (this.loginInProgress) {
          if (this.myAccount.otp_enabled) {
            this.otpInProgress = true;
          } else {
            // Load the user's organization config, inbox counts, and locations.
            this.orgConfigService.loadOrganizationConfig().then();
            this.inboxService.loadInboxCounts(false).then();
            this.locationService.loadLocations().then();
            this.selectLocationInProgress = true;
          }
        } else {
          if (this.myAccount.otp_enabled && (this.apiService.otpToken === null)) {
            this.otpInProgress = true;
          }
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.logout();
        } else {
          this.myAccountServerError = reason.status;
        }
      });
  }

  /**
   * Merge the given fields into the cached account, preserving the logged-in
   * user's identity and permissions.
   *
   * In PIN mode, unsafe requests (PATCH/POST) to `my-account/` are
   * authenticated as the PIN user (the staff member whose PIN was entered), so
   * their responses describe the PIN user — not the logged-in user. Assigning
   * such a response straight to `myAccount` would make permission-gated UI
   * (e.g. the "Reporting" navbar link) reflect the PIN user instead of the
   * logged-in user (PRO-1213). Instead, each caller passes only the fields it
   * meant to change; everything else (permissions, email, etc.) stays as the
   * logged-in user's. In non-PIN mode the response is the logged-in user's own
   * account, so the merged fields match it and behaviour is unchanged.
   *
   * @param updatedFields - The fields the caller intended to change.
   * @return The updated cached account.
   */
  private applyMyAccountUpdate(updatedFields: Partial<MyAccount>): MyAccount {
    this.myAccount = {
      ...this.myAccount,
      ...updatedFields,
    };
    return this.myAccount;
  }

  /**
   * Update the user's account info.
   *
   * @param newAccountInfo - The myAccount object, with updated values.
   * @return The new myAccount object returned from the API.
   */
  public updateMyAccount(newAccountInfo: MyAccount): Promise<MyAccount> {
    return this.apiService
      .patchToAPI<MyAccount>(
        environment.api_url + 'my-account/',
        {
          first_name: newAccountInfo.first_name,
          last_name: newAccountInfo.last_name,
          default_gym_id: newAccountInfo.default_gym_id,
        }
      )
      .then((accountData: MyAccount) =>
        // Adopt only the fields this edit owns; preserve the logged-in user's
        // permissions/identity. See applyMyAccountUpdate.
        this.applyMyAccountUpdate({
          first_name: accountData.first_name,
          last_name: accountData.last_name,
          default_gym_id: accountData.default_gym_id,
          default_gym_name: accountData.default_gym_name,
          default_gym_url: accountData.default_gym_url,
          default_gym_scheduler_first_day_of_week:
            accountData.default_gym_scheduler_first_day_of_week,
          default_gym_default_staff_site_events_view:
            accountData.default_gym_default_staff_site_events_view,
        })
      );
  }

  /**
   * Update the user's password.
   *
   * @param previousPassword - The user's current password.
   * @param newPassword - The user's new password.
   * @return The new myAccount object returned from the API.
   */
  public changeMyAccountPassword(previousPassword: string,
                                 newPassword: string): Promise<void> {
    return this.apiService
      .postToAPI<APITokenResponse>(
        'my-account/change-password/',
        {
          previous_password: previousPassword,
          new_password: newPassword
        }
      )
      .then((tokenData: APITokenResponse) => {
        // Save the new token to local storage.
        this.apiService.token = tokenData.token;
      });
  }

  public updateMyAccountFavoriteReports(favoriteReports: string[]): Promise<MyAccount> {
    return this.apiService
      .patchToAPI<MyAccount>(
        environment.api_url + 'my-account/',
        {
          favorite_reports: favoriteReports,
        }
      )
      .then((accountData: MyAccount) =>
        // See applyMyAccountUpdate: preserve the logged-in user's permissions.
        this.applyMyAccountUpdate({
          favorite_reports: accountData.favorite_reports,
        })
      );
  }

  public setDefaultReportViewId(reportType: string, viewId: number): Promise<MyAccount> {
    return this.apiService
      .postToAPI<MyAccount>(
        'my-account/set-default-report-view-id/',
        {
          report_type: reportType,
          view_id: viewId,
        }
      )
      .then((accountData: MyAccount) =>
        // See applyMyAccountUpdate: preserve the logged-in user's permissions.
        this.applyMyAccountUpdate({
          default_report_view_ids: accountData.default_report_view_ids,
        })
      );
  }

  public setOrganizationDefaultReportViewId(reportType: string, viewId: number): Promise<MyAccount> {
    return this.apiService
      .postToAPI<MyAccount>(
        'organization-config/set-default-report-view-id/',
        {
          report_type: reportType,
          view_id: viewId,
        }
      )
      .then((accountData: MyAccount) =>
        // See applyMyAccountUpdate: preserve the logged-in user's permissions.
        this.applyMyAccountUpdate({
          organization_default_report_view_ids: accountData.organization_default_report_view_ids,
        })
      );
  }

  /**
   * Generate a new one-time password secret for the user (used for two-factor
   * authentication).
   */
  public generateOTP(): Promise<GenerateOTPResponse> {
    return this.apiService
      .postToAPI<GenerateOTPResponse>(
        'user/otp/generate/',
        {}
      );
  }

  /**
   * Verify the user's newly-generated one-time password (used for two-factor
   * authentication).
   */
  public verifyOTP(token: string): Promise<void> {
    return this.apiService
      .postToAPI<any>(
        'user/otp/verify/',
        {token}
      )
      .then();
  }

  /**
   * Remove the user's one-time password secret (used for two-factor
   * authentication).
   */
  public removeOTP(): Promise<void> {
    return this.apiService
      .postToAPI<any>(
        'user/otp/remove/',
        {}
      )
      .then();
  }

  /**
   * Update the user's default location via the API.
   *
   * @param locationId - The new default location ID.
   * @return A promise which resolves when the request completes.
   */
  public setDefaultLocation(locationId: number): Promise<void> {
    return this.apiService
      .patchToAPI<MyAccount>(
        environment.api_url + 'my-account/',
        {default_gym_id: locationId}
      )
      .then((accountData: MyAccount) => {
        this.selectLocationInProgress = false;
        // Adopt only the selected location's fields; the response reports them
        // correctly for the chosen location in both PIN and non-PIN mode. See
        // applyMyAccountUpdate for why we don't take the whole response.
        this.applyMyAccountUpdate({
          default_gym_id: accountData.default_gym_id,
          default_gym_name: accountData.default_gym_name,
          default_gym_url: accountData.default_gym_url,
          default_gym_scheduler_first_day_of_week:
            accountData.default_gym_scheduler_first_day_of_week,
          default_gym_default_staff_site_events_view:
            accountData.default_gym_default_staff_site_events_view,
        });
      });
  }

  /**
   * Get the user's default location from the cashed location list.
   */
  public getDefaultLocation(): CapitanLocation {
    const locations = this.locationService.getLocations();

    if (locations === null) { return null; }

    for (const location of this.locationService.getLocations()) {
      if (location.id === this.myAccount.default_gym_id) {
        return location;
      }
    }
    return null;
  }

  public getSelectLocationInProgress(): boolean {
    return this.selectLocationInProgress;
  }

  public getOtpInProgress(): boolean {
    return this.otpInProgress;
  }

  /**
   * Get the user's authentication token.
   */
  public getToken(): string {
    return this.apiService.token;
  }

  public enterCustomerFacingMode() {
    this.inCustomerFacingMode = true;
  }

  public getCustomerFacingMode(): boolean {
    return this.inCustomerFacingMode;
  }
}
